import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Select,
  DatePicker,
  Button,
  Table,
  Space,
  Tag,
  Typography,
  Spin,
  message,
} from 'antd';
import {
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  TrophyOutlined,
  BarChartOutlined,
  ReloadOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

dayjs.locale('fr');

interface RevenueData {
  period: string;
  year: number;
  date_range: {
    start_date: string;
    end_date: string;
  };
  summary: {
    total_revenue: number;
    total_billed: number;
    total_outstanding: number;
    invoices_issued: number;
    invoices_paid: number;
    payment_rate: number;
  };
  revenue_by_month: Array<{
    month: string;
    total_billed: number;
    total_paid: number;
    invoices_count: number;
    paid_invoices_count: number;
  }>;
  revenue_by_plan: Array<{
    plan_name: string;
    plan_price: number;
    invoices_count: number;
    total_billed: number;
    total_paid: number;
    payment_rate: number;
  }>;
  top_customers: Array<{
    company_id: number;
    company_name: string;
    invoices_count: number;
    total_billed: number;
    total_paid: number;
  }>;
}

const RevenueReportDashboard: React.FC = () => {
  const [revenueData, setRevenueData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  // Couleurs pour les graphiques
  const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];

  // Fonctions utilitaires pour formater les montants avec séparateurs de milliers
  const formatAmount = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) return '0';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(amount));
  };

  const formatCurrency = (amount: number | null | undefined): string => {
    const formatted = formatAmount(amount);
    return `${formatted} XOF`;
  };

  // Formater un pourcentage de manière sûre.
  // IMPORTANT: MySQL renvoie les DECIMAL/ROUND() sous forme de chaînes (ex: "85.50"),
  // donc on coerce systématiquement en nombre avant .toFixed() pour éviter
  // "TypeError: x.toFixed is not a function".
  const formatPercent = (
    value: number | string | null | undefined,
    digits = 1
  ): string => {
    const n = Number(value);
    return isNaN(n) ? '0.0' : n.toFixed(digits);
  };

  // Charger les données de CA
  const fetchRevenueData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        period,
        ...(dateRange
          ? {
              start_date: dateRange[0].format('YYYY-MM-DD'),
              end_date: dateRange[1].format('YYYY-MM-DD'),
            }
          : { year: year.toString() }),
      });

      const response = await fetch(`/api/subscription/reports/revenue?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setRevenueData(data.data);
      } else {
        message.error('Erreur lors du chargement des données');
      }
    } catch (error) {
      console.error('Erreur:', error);
      message.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRevenueData();
  }, [period, year, dateRange]);

  // Formater les données pour les graphiques
  // Coercition en nombre: les agrégats SQL (SUM/ROUND) arrivent en chaînes,
  // Recharts a besoin de vrais nombres pour calculer la hauteur des barres/secteurs.
  const monthlyRevenueData = revenueData?.revenue_by_month.map((item) => ({
    month: dayjs(item.month).format('MMM YY'),
    Facturé: Number(item.total_billed) || 0,
    Payé: Number(item.total_paid) || 0,
  })) || [];

  const planDistributionData = revenueData?.revenue_by_plan.map((plan, index) => ({
    name: plan.plan_name,
    value: Number(plan.total_paid) || 0,
    count: Number(plan.invoices_count) || 0,
    color: COLORS[index % COLORS.length],
  })) || [];

  // Colonnes du tableau Top Clients
  const topCustomersColumns = [
    {
      title: 'Rang',
      key: 'rank',
      width: 60,
      render: (_: any, __: any, index: number) => (
        <strong className="text-blue-600">#{index + 1}</strong>
      ),
    },
    {
      title: 'Entreprise',
      dataIndex: 'company_name',
      key: 'company_name',
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: 'Factures',
      dataIndex: 'invoices_count',
      key: 'invoices_count',
      width: 100,
      align: 'center' as const,
      render: (count: number) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: 'Total Facturé',
      dataIndex: 'total_billed',
      key: 'total_billed',
      width: 150,
      align: 'right' as const,
      render: (amount: number) => (
        <span>{formatCurrency(amount)}</span>
      ),
    },
    {
      title: 'Total Payé',
      dataIndex: 'total_paid',
      key: 'total_paid',
      width: 150,
      align: 'right' as const,
      render: (amount: number) => (
        <strong className="text-green-600">
          {formatCurrency(amount)}
        </strong>
      ),
    },
  ];

  // Colonnes du tableau Revenus par Plan
  const planRevenueColumns = [
    {
      title: 'Plan',
      dataIndex: 'plan_name',
      key: 'plan_name',
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Tarif catalogue / Warehouse',
      dataIndex: 'plan_price',
      key: 'plan_price',
      width: 190,
      align: 'right' as const,
      render: (price: number) => formatCurrency(price),
    },
    {
      title: 'Factures',
      dataIndex: 'invoices_count',
      key: 'invoices_count',
      width: 100,
      align: 'center' as const,
      render: (count: number) => formatAmount(count),
    },
    {
      title: 'Total Facturé',
      dataIndex: 'total_billed',
      key: 'total_billed',
      width: 150,
      align: 'right' as const,
      render: (amount: number) => formatCurrency(amount),
    },
    {
      title: 'Total Payé',
      dataIndex: 'total_paid',
      key: 'total_paid',
      width: 150,
      align: 'right' as const,
      render: (amount: number) => (
        <strong className="text-green-600">
          {formatCurrency(amount)}
        </strong>
      ),
    },
    {
      title: 'Taux de Paiement',
      dataIndex: 'payment_rate',
      key: 'payment_rate',
      width: 120,
      align: 'center' as const,
      render: (rate: number | string) => {
        const value = Number(rate) || 0;
        return (
          <Tag color={value >= 80 ? 'green' : value >= 50 ? 'orange' : 'red'}>
            {formatPercent(value)}%
          </Tag>
        );
      },
    },
  ];

  if (loading && !revenueData) {
    return (
      <div className="text-center py-12">
        <Spin size="large" />
        <div className="mt-4">
          <Text type="secondary">Chargement des données...</Text>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Barre de contrôle */}
      <Card className="mb-4">
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={4}>
            <Select
              value={period}
              onChange={setPeriod}
              className="w-full"
            >
              <Option value="monthly">Mensuel</Option>
              <Option value="yearly">Annuel</Option>
            </Select>
          </Col>

          {!dateRange && (
            <Col xs={24} sm={8} md={4}>
              <Select
                value={year}
                onChange={setYear}
                className="w-full"
              >
                {[...Array(5)].map((_, i) => {
                  const y = new Date().getFullYear() - i;
                  return (
                    <Option key={y} value={y}>
                      {y}
                    </Option>
                  );
                })}
              </Select>
            </Col>
          )}

          <Col xs={24} sm={12} md={8}>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
              format="DD/MM/YYYY"
              className="w-full"
              placeholder={['Date début', 'Date fin']}
            />
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchRevenueData}
                loading={loading}
              >
                Actualiser
              </Button>
              <Button
                type="primary"
                icon={<FilePdfOutlined />}
                disabled
              >
                Exporter PDF
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {revenueData && (
        <>
          {/* Métriques principales */}
          <Row gutter={[16, 16]} className="mb-4">
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Chiffre d'Affaires"
                  value={revenueData.summary.total_revenue || 0}
                  formatter={(value) => formatCurrency(value as number)}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                />
                <div className="mt-2">
                  <Text type="secondary" className="text-xs">
                    {formatAmount(revenueData.summary.invoices_paid)} facture(s) payée(s)
                  </Text>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Facturé"
                  value={revenueData.summary.total_billed || 0}
                  formatter={(value) => formatCurrency(value as number)}
                  prefix={<BarChartOutlined />}
                  valueStyle={{ color: '#1890ff', fontSize: '24px' }}
                />
                <div className="mt-2">
                  <Text type="secondary" className="text-xs">
                    {formatAmount(revenueData.summary.invoices_issued)} facture(s) émise(s)
                  </Text>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="En Attente"
                  value={revenueData.summary.total_outstanding || 0}
                  formatter={(value) => formatCurrency(value as number)}
                  valueStyle={{ color: '#faad14', fontSize: '24px' }}
                />
                <div className="mt-2">
                  <Text type="secondary" className="text-xs">
                    {formatAmount(revenueData.summary.invoices_issued - revenueData.summary.invoices_paid)} facture(s)
                  </Text>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Taux de Paiement"
                  value={Number(revenueData.summary.payment_rate) || 0}
                  precision={1}
                  suffix="%"
                  prefix={
                    Number(revenueData.summary.payment_rate) >= 75 ? (
                      <ArrowUpOutlined />
                    ) : (
                      <ArrowDownOutlined />
                    )
                  }
                  valueStyle={{
                    color: Number(revenueData.summary.payment_rate) >= 75 ? '#52c41a' : '#faad14',
                    fontSize: '24px',
                  }}
                />
              </Card>
            </Col>
          </Row>

          {/* Graphiques */}
          <Row gutter={[16, 16]} className="mb-4">
            {/* Évolution mensuelle du CA */}
            <Col xs={24} lg={16}>
              <Card title="Évolution du Chiffre d'Affaires" className="h-full">
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={monthlyRevenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip
                      formatter={(value: number) => formatCurrency(value as number)}
                    />
                    <Legend />
                    <Bar dataKey="Facturé" fill="#1890ff" name="Total Facturé" />
                    <Bar dataKey="Payé" fill="#52c41a" name="Total Payé" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>

            {/* Répartition par plan */}
            <Col xs={24} lg={8}>
              <Card title="Répartition par Plan" className="h-full">
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={planDistributionData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {planDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value: number) => formatCurrency(value as number)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          {/* Tableaux de données */}
          <Row gutter={[16, 16]}>
            {/* Top Clients */}
            <Col xs={24} lg={12}>
              <Card
                title={
                  <span>
                    <TrophyOutlined className="mr-2 text-yellow-500" />
                    Top 10 Clients par Chiffre d'Affaires
                  </span>
                }
              >
                <Table
                  columns={topCustomersColumns}
                  dataSource={revenueData.top_customers}
                  pagination={false}
                  size="small"
                  rowKey="company_id"
                  scroll={{ y: 400 }}
                />
              </Card>
            </Col>

            {/* Revenus par Plan */}
            <Col xs={24} lg={12}>
              <Card
                title={
                  <span>
                    <BarChartOutlined className="mr-2 text-blue-500" />
                    Revenus par Plan d'Abonnement
                  </span>
                }
              >
                <Table
                  columns={planRevenueColumns}
                  dataSource={revenueData.revenue_by_plan}
                  pagination={false}
                  size="small"
                  rowKey="plan_name"
                  scroll={{ y: 400 }}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default RevenueReportDashboard;
